import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    AvailableSmartAssistantImportDTO,
    CompanySmartAssistantManagementControllerService,
    EmployeeExportManagementControllerService,
    SmartAssistantImportSummaryDTO,
    SolutionPartnerSmartAssistantControllerService
} from '@iota/inh-core-v1-management-client';
import * as saveAs from 'file-saver';
import { finalize, Observable, tap } from 'rxjs';
import { RouteParamsService } from 'src/app/shared/services/route-params.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { getFilenameHeader } from 'src/app/shared/utils';
import { createAsyncResource } from 'src/app/shared/utils/async-state.util';
import { toSmartAssistantOwner, toSmartAssistantReportsTable, toSmartAssistantTiles } from './smart-assistant.mapper';

@Injectable()
export class SmartAssistantService {
    private readonly companyApi = inject(CompanySmartAssistantManagementControllerService);
    private readonly partnerApi = inject(SolutionPartnerSmartAssistantControllerService);
    private readonly employeeExportApi = inject(EmployeeExportManagementControllerService);
    private readonly toastService = inject(ToastService);

    readonly owner = toSmartAssistantOwner(inject(ActivatedRoute).parent?.snapshot.data.mode);
    readonly companyId = inject(RouteParamsService).companyId;

    private readonly availableImports = createAsyncResource<AvailableSmartAssistantImportDTO[]>(
        () => this.getAvailableImports(this.companyId()!),
        { lazy: true }
    );
    private readonly latestImports = createAsyncResource<SmartAssistantImportSummaryDTO[]>(
        () => this.findLatestImports(this.companyId()!),
        { lazy: true }
    );

    readonly tiles = computed(() => toSmartAssistantTiles(this.availableImports.data() ?? []));
    readonly reportsTable = computed(() => toSmartAssistantReportsTable(this.latestImports.data() ?? []));
    private readonly backupLoadingState = signal(false);
    readonly backupLoading = this.backupLoadingState.asReadonly();
    readonly availableImportsLoading = this.availableImports.loading;
    readonly latestImportsLoading = this.latestImports.loading;

    constructor() {
        effect(() => {
            if (this.companyId()) {
                untracked(() => {
                    this.availableImports.reload();
                    this.latestImports.reload();
                });
            }
        });
    }

    reloadLatestImports(): void {
        if (this.companyId()) {
            this.latestImports.reload();
        }
    }

    downloadReport(importId: string): void {
        this.downloadReportFile(this.companyId()!, importId)
            .pipe(tap((blob) => saveAs(blob, 'processing-report.xlsx')))
            .subscribe({ error: () => this.toastService.showError($localize`Failed to download processing report.`) });
    }

    downloadEmployeeBackup(): void {
        const companyId = this.companyId();
        if (!companyId || this.backupLoading()) {
            return;
        }
        this.backupLoadingState.set(true);
        this.employeeExportApi
            ._export(companyId, 'response')
            .pipe(
                tap((response) => saveAs(response.body, getFilenameHeader(response, 'employee-backup.xlsx'))),
                finalize(() => this.backupLoadingState.set(false))
            )
            .subscribe({ error: () => this.toastService.showError($localize`Failed to download the employee backup.`) });
    }

    private getAvailableImports(companyId: string): Observable<AvailableSmartAssistantImportDTO[]> {
        return this.owner === 'COMPANY' ? this.companyApi.getAvailableImports(companyId) : this.partnerApi.getAvailableImports(companyId);
    }

    private findLatestImports(companyId: string): Observable<SmartAssistantImportSummaryDTO[]> {
        return this.owner === 'COMPANY'
            ? this.companyApi.findLatestImports(companyId, 10)
            : this.partnerApi.findLatestImports(companyId, 10);
    }

    private downloadReportFile(companyId: string, importId: string): Observable<Blob> {
        return this.owner === 'COMPANY'
            ? this.companyApi.downloadReport(companyId, importId)
            : this.partnerApi.downloadReport(companyId, importId);
    }
}
